import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import {
  CheckRounded,
  CloseRounded,
  CloudUpload as CloudUploadIcon,
  FileCopyOutlined as FileCopyIcon,
  GetApp as GetAppIcon,
  ListAlt as ListAltIcon,
} from "@material-ui/icons";
import { toast } from "react-hot-toast";
import {
  downloadIdTemplate,
  parseIdsFromFile,
  parseIdsFromText,
} from "../../../shared/utils/parse-id-list";

const MAX_FILAS_VISIBLES = 300;
const PREFIJO_BORRADOR = "importador-masivo:";

const leerBorrador = (clave) => {
  if (!clave) return null;
  try {
    const raw = sessionStorage.getItem(`${PREFIJO_BORRADOR}${clave}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const guardarBorrador = (clave, borrador) => {
  if (!clave) return;
  try {
    sessionStorage.setItem(`${PREFIJO_BORRADOR}${clave}`, JSON.stringify(borrador));
  } catch {
    // La sesión puede estar llena o no disponible.
  }
};

const trimId = (id) => id.trim();
const etiquetaPorDefecto = (item) => item?.title ?? item?.nombre ?? "";

const ESTADOS = {
  nuevo: { label: "Nuevo", color: "#2f8270", background: "#e6f7f1" },
  actualizar: { label: "Se actualizará", color: "#1565c0", background: "#e3f2fd" },
  existente: { label: "Ya existe", color: "#555", background: "#eeeeee" },
  repetido: { label: "Repetido en la lista", color: "#b36b00", background: "#fff3e0" },
  no_encontrado: { label: "No encontrado", color: "#a8261e", background: "#fdecea" },
};

/**
 * Importador masivo de identificadores reutilizable.
 * Permite pegar celdas desde Excel (o una lista separada por comas, punto y coma,
 * espacios o saltos de línea) o subir un archivo CSV/XLS/XLSX, y muestra una
 * previsualización con elementos nuevos, repetidos, existentes y no encontrados
 * antes de confirmar.
 *
 * @param {{
 *  title?: string,
 *  description?: React.ReactNode,
 *  idHeader: string,                 Encabezado de la plantilla / columna a leer
 *  idLabel?: string,                 Nombre de la columna ID en la previsualización
 *  templateExamples?: string[],
 *  templateFileName?: string,
 *  splitOnSpaces?: boolean,          false si los valores pueden tener espacios
 *  normalizeId?: (id: string) => string,
 *  resolveItem: (id: string) => any, Retorna el elemento del catálogo o null
 *  itemLabel?: (item: any) => string,
 *  getExistingState?: (id: string, item: any) => null | { status: "existente" | "actualizar", mensaje?: string },
 *  existingLabel?: string,
 *  confirmLabel?: (count: number) => string,
 *  onConfirm: (rows: Array<{ id: string, item: any, status: string }>) => Promise<string | void>,
 *  missingMessage?: string,
 *  disabled?: boolean,
 *  disabledReason?: string,
 *  persistKey?: string,          Si se indica, el borrador se guarda por clave y sobrevive al cambiar de contexto
 * }} props
 */
const ImportadorMasivo = ({
  title = "Importación masiva",
  description,
  idHeader,
  idLabel = "ID",
  templateExamples = [],
  templateFileName = "plantilla",
  splitOnSpaces = true,
  normalizeId = trimId,
  resolveItem,
  itemLabel = etiquetaPorDefecto,
  getExistingState,
  existingLabel = ESTADOS.existente.label,
  confirmLabel = (count) => `Confirmar (${count})`,
  onConfirm,
  missingMessage = "No existe en el catálogo",
  disabled = false,
  disabledReason,
  persistKey,
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const claveRenderRef = useRef(persistKey);
  const claveAnteriorRef = useRef(persistKey);
  claveRenderRef.current = persistKey;

  const [modo, setModo] = useState(() =>
    leerBorrador(persistKey)?.modo === "archivo" ? "archivo" : "pegar"
  );
  const [texto, setTexto] = useState(() => leerBorrador(persistKey)?.texto ?? "");
  const [archivo, setArchivo] = useState(() => leerBorrador(persistKey)?.archivo ?? null);
  const [idsArchivo, setIdsArchivo] = useState(() => {
    const ids = leerBorrador(persistKey)?.idsArchivo;
    return Array.isArray(ids) ? ids : [];
  });
  const [filtro, setFiltro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(() => leerBorrador(persistKey)?.resultado ?? null);

  useEffect(() => {
    if (!persistKey) return;

    if (claveAnteriorRef.current !== persistKey) {
      claveAnteriorRef.current = persistKey;
      const draft = leerBorrador(persistKey);
      setModo(draft?.modo === "archivo" ? "archivo" : "pegar");
      setTexto(typeof draft?.texto === "string" ? draft.texto : "");
      setArchivo(draft?.archivo ?? null);
      setIdsArchivo(Array.isArray(draft?.idsArchivo) ? draft.idsArchivo : []);
      setFiltro(null);
      setResultado(draft?.resultado ?? null);
      setEnviando(false);
      return;
    }

    guardarBorrador(persistKey, { modo, texto, archivo, idsArchivo, resultado });
  }, [persistKey, modo, texto, archivo, idsArchivo, resultado]);

  const idsEntrada = useMemo(
    () =>
      modo === "pegar"
        ? parseIdsFromText(texto, { header: idHeader, splitOnSpaces })
        : idsArchivo,
    [modo, texto, idsArchivo, idHeader, splitOnSpaces]
  );

  const filas = useMemo(() => {
    const vistos = new Set();
    return idsEntrada.map((raw, index) => {
      const id = normalizeId(raw);
      const item = resolveItem(id);
      let status = "nuevo";
      let mensaje = "Listo para importar";

      if (vistos.has(id.toLowerCase())) {
        status = "repetido";
        mensaje = "Aparece más de una vez; se importará una sola vez";
      } else if (!item) {
        status = "no_encontrado";
        mensaje = missingMessage;
      } else {
        const existente = getExistingState?.(id, item);
        if (existente) {
          status = existente.status;
          mensaje = existente.mensaje ?? mensaje;
        }
      }
      vistos.add(id.toLowerCase());
      return { index, id, item, status, mensaje };
    });
  }, [idsEntrada, normalizeId, resolveItem, getExistingState, missingMessage]);

  const conteo = useMemo(
    () =>
      filas.reduce(
        (acc, fila) => ({ ...acc, [fila.status]: acc[fila.status] + 1 }),
        { nuevo: 0, actualizar: 0, existente: 0, repetido: 0, no_encontrado: 0 }
      ),
    [filas]
  );

  const validas = useMemo(
    () => filas.filter((f) => f.status === "nuevo" || f.status === "actualizar"),
    [filas]
  );

  const filasFiltradas = filtro ? filas.filter((f) => f.status === filtro) : filas;

  const limpiar = () => {
    setTexto("");
    setArchivo(null);
    setIdsArchivo([]);
    setFiltro(null);
  };

  const handleArchivo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const ids = await parseIdsFromFile(file, { header: idHeader });
      if (!ids.length) toast.error("El archivo no contiene valores en la columna esperada");
      setArchivo(file.name);
      setIdsArchivo(ids);
      setFiltro(null);
      setResultado(null);
    } catch (error) {
      console.error(error);
      toast.error("No se pudo leer el archivo. Verifica que sea CSV o Excel.");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const copiarNoEncontrados = () => {
    const ids = filas.filter((f) => f.status === "no_encontrado").map((f) => f.id);
    navigator.clipboard
      .writeText(ids.join("\n"))
      .then(() => toast.success(`${ids.length} IDs copiados al portapapeles`));
  };

  const confirmar = async () => {
    const clave = persistKey;
    setEnviando(true);
    try {
      const mensaje = await onConfirm(validas);
      const textoResultado = mensaje || `${validas.length} elementos procesados`;
      if (clave && claveRenderRef.current !== clave) {
        guardarBorrador(clave, {
          modo: "pegar",
          texto: "",
          archivo: null,
          idsArchivo: [],
          resultado: textoResultado,
        });
        return;
      }
      setResultado(textoResultado);
      limpiar();
    } catch (error) {
      if (!clave || claveRenderRef.current === clave) {
        toast.error(error?.message || "No se pudo completar la importación");
      }
    } finally {
      if (!clave || claveRenderRef.current === clave) setEnviando(false);
    }
  };

  const etiquetaEstado = (status) =>
    status === "existente" ? existingLabel : ESTADOS[status].label;

  return (
    <div className={classes.wrapper}>
      <div className="d-flex align-items-start justify-content-between flex-wrap" style={{ gap: 12 }}>
        <div>
          <div className="admin-main-title-dark-gray">{title}</div>
          {description && <div className="admin-muted-text-small mt-1">{description}</div>}
        </div>
        <Button
          variant="outlined"
          size="small"
          startIcon={<GetAppIcon />}
          onClick={() =>
            downloadIdTemplate({
              header: idHeader,
              examples: templateExamples,
              fileName: templateFileName,
            })
          }
        >
          Descargar plantilla
        </Button>
      </div>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={modo}
        onChange={(_, value) => value && setModo(value)}
        className="mt-3 mb-2"
      >
        <ToggleButton value="pegar" className={classes.toggle}>
          <ListAltIcon fontSize="small" className="mr-1" /> Pegar desde Excel
        </ToggleButton>
        <ToggleButton value="archivo" className={classes.toggle}>
          <CloudUploadIcon fontSize="small" className="mr-1" /> Subir archivo
        </ToggleButton>
      </ToggleButtonGroup>

      {modo === "pegar" ? (
        <TextField
          multiline
          minRows={5}
          maxRows={12}
          fullWidth
          variant="outlined"
          value={texto}
          disabled={disabled || enviando}
          onChange={(e) => {
            setTexto(e.target.value);
            setResultado(null);
          }}
          placeholder={`Pega aquí la columna copiada de Excel (con o sin el encabezado "${idHeader}"), o escribe los valores separados por saltos de línea, comas${
            splitOnSpaces ? ", espacios" : ""
          } o punto y coma.`}
          className={classes.textarea}
        />
      ) : (
        <div className={classes.fileBox}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleArchivo}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || enviando}
            classes={{ root: classes.uploadBtn }}
          >
            {archivo ? "Cambiar archivo" : "Seleccionar archivo"}
          </Button>
          {archivo ? (
            <Chip label={archivo} size="small" onDelete={enviando ? undefined : limpiar} />
          ) : (
            <span className="admin-muted-text-small">
              CSV o Excel. Se lee la columna <code>{idHeader}</code> de la primera hoja (o la primera
              columna si no hay encabezado).
            </span>
          )}
        </div>
      )}

      {disabled && disabledReason && (
        <div className="admin-muted-text-small mt-2" style={{ color: "#a8261e" }}>
          {disabledReason}
        </div>
      )}

      {resultado && filas.length === 0 && (
        <div className={classes.resultBox}>
          <CheckRounded style={{ color: "#2f8270" }} /> {resultado}
        </div>
      )}

      {filas.length > 0 && (
        <>
          <div className={classes.summaryRow}>
            <Chip
              size="small"
              variant={filtro === null ? "default" : "outlined"}
              label={`Total: ${filas.length}`}
              onClick={() => setFiltro(null)}
            />
            {Object.keys(ESTADOS).map(
              (status) =>
                conteo[status] > 0 && (
                  <Chip
                    key={status}
                    size="small"
                    clickable
                    label={`${etiquetaEstado(status)}: ${conteo[status]}`}
                    onClick={() => setFiltro(filtro === status ? null : status)}
                    style={{
                      background: ESTADOS[status].background,
                      color: ESTADOS[status].color,
                      border: filtro === status ? `1px solid ${ESTADOS[status].color}` : "1px solid transparent",
                    }}
                  />
                )
            )}
            {conteo.no_encontrado > 0 && (
              <Tooltip title="Copiar los IDs no encontrados para revisarlos">
                <Button size="small" startIcon={<FileCopyIcon />} onClick={copiarNoEncontrados}>
                  Copiar no encontrados
                </Button>
              </Tooltip>
            )}
          </div>

          <div className={classes.tableWrapper}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th style={{ width: 140 }}>{idLabel}</th>
                  <th>Nombre</th>
                  <th style={{ width: 170 }}>Estado</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {filasFiltradas.slice(0, MAX_FILAS_VISIBLES).map((fila) => (
                  <tr key={`${fila.id}-${fila.index}`}>
                    <td>{fila.index + 1}</td>
                    <td>
                      <code>{fila.id}</code>
                    </td>
                    <td>{fila.item ? itemLabel(fila.item) : "—"}</td>
                    <td>
                      <span
                        className={classes.badge}
                        style={{
                          color: ESTADOS[fila.status].color,
                          background: ESTADOS[fila.status].background,
                        }}
                      >
                        {etiquetaEstado(fila.status)}
                      </span>
                    </td>
                    <td className="admin-muted-text-small">{fila.mensaje}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filasFiltradas.length > MAX_FILAS_VISIBLES && (
            <div className="admin-muted-text-small mt-1">
              Mostrando {MAX_FILAS_VISIBLES} de {filasFiltradas.length} filas. Todas se tendrán en
              cuenta al confirmar.
            </div>
          )}

          <div className="d-flex justify-content-end align-items-center mt-3" style={{ gap: 12 }}>
            <span className="admin-muted-text-small">
              {validas.length === 0
                ? "No hay elementos para importar"
                : `Se procesarán ${validas.length} de ${filas.length}`}
            </span>
            <Button
              variant="outlined"
              onClick={limpiar}
              disabled={enviando}
              startIcon={<CloseRounded />}
            >
              Limpiar
            </Button>
            <Button
              variant="contained"
              onClick={confirmar}
              disabled={disabled || enviando || validas.length === 0}
              startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <CheckRounded />}
              classes={{ root: classes.confirmBtn }}
            >
              {enviando ? "Procesando…" : confirmLabel(validas.length)}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ImportadorMasivo;

const useStyles = makeStyles({
  wrapper: {
    background: "#fafafa",
    border: "1px solid #e8e8e8",
    borderRadius: 6,
    padding: "16px 18px",
  },
  toggle: {
    textTransform: "none",
    padding: "4px 12px",
    "&.Mui-selected": { background: "#5fbeaa", color: "#fff" },
    "&.Mui-selected:hover": { background: "#4ea493" },
  },
  textarea: {
    background: "#fff",
    "& textarea": { fontFamily: "monospace", fontSize: "0.9em" },
  },
  fileBox: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    padding: 14,
    background: "#fff",
    border: "1px dashed #cfcfcf",
    borderRadius: 4,
  },
  uploadBtn: {
    background: "#5fbeaa",
    color: "#fff",
    "&:hover": { backgroundColor: "#4ea493" },
  },
  confirmBtn: {
    background: "#81c868",
    color: "#fff",
    "&:hover": { backgroundColor: "#6fb558" },
    "&.Mui-disabled": { background: "#cdeac3", color: "#fff" },
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    margin: "12px 0 10px",
  },
  tableWrapper: {
    maxHeight: 320,
    overflow: "auto",
    border: "1px solid #e8e8e8",
    borderRadius: 4,
    background: "#fff",
  },
  table: {
    width: "100%",
    fontSize: "0.85em",
    borderCollapse: "collapse",
    "& th, & td": {
      padding: "6px 10px",
      borderBottom: "1px solid #f0f0f0",
      textAlign: "left",
    },
    "& thead th": {
      background: "#f5f5f5",
      fontWeight: 600,
      position: "sticky",
      top: 0,
      zIndex: 1,
    },
  },
  badge: {
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: "0.8em",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  resultBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    padding: 12,
    background: "#fff",
    border: "1px solid #cdeac3",
    borderRadius: 4,
  },
});
